import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalBody } from "reactstrap";
import Loader from "react-loader-spinner";
import closeIcon from "../../assets/images/CouselorMessages/SingleChat/close.png";
import "./OtpVerifyModal.css";

const OTP_LENGTH = 6;
const TIMER_SECONDS = 60;

const OtpVerifyModal = ({
  isOpen,
  onCancel,
  onSubmit,
  onResend,
  submitting = false,
  resending = false,
  resendTick = 0,
}) => {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(TIMER_SECONDS);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setDigits(Array(OTP_LENGTH).fill(""));
    setSecondsLeft(TIMER_SECONDS);
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
    return () => clearInterval(timer);
  }, [isOpen, resendTick]);

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  const otpValue = digits.join("");
  const canSubmit = otpValue.length === OTP_LENGTH && !submitting;
  const canResend = secondsLeft === 0 && !resending;

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec < 10 ? "0" : ""}${sec}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      backdrop="static"
      keyboard={false}
      centered
      className="otp-verify-modal"
    >
      <ModalBody className="otp-verify-body">
        <button type="button" className="otp-verify-close" onClick={onCancel} aria-label="Close">
          <img src={closeIcon} alt="" />
        </button>
        <h5 className="otp-verify-title">Enter Verification Code</h5>
        <p className="otp-verify-subtitle">
          We sent a 6-digit code to your registered email. Enter it below to continue.
        </p>

        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <div className="otp-digit-row" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                name={`otp-verify-digit-${i}`}
                id={`otp-verify-digit-${i}`}
                className="otp-digit-input"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                disabled={submitting}
              />
            ))}
          </div>
        </form>

        <div className="otp-timer-row">
          {secondsLeft > 0 ? (
            <span className="otp-timer-text">Resend code in {formatTime(secondsLeft)}</span>
          ) : (
            <button
              type="button"
              className="otp-resend-link"
              onClick={onResend}
              disabled={!canResend}
            >
              {resending ? "Sending..." : "Resend OTP"}
            </button>
          )}
        </div>

        <div className="otp-action-row">
          <button type="button" className="otp-btn otp-btn-cancel" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="otp-btn otp-btn-submit"
            onClick={() => onSubmit(otpValue)}
            disabled={!canSubmit}
          >
            {submitting ? (
              <Loader type="ThreeDots" color="#ffffff" height={20} width={40} />
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default OtpVerifyModal;
